import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Avatar, Badge, Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useStyles } from '../styles/memberStyles';
import { stringAvatar } from '../../../../utils/avatarCreator';
import { GUEST, PROJECT_EDITOR_ROLE, ROLE_LEVEL } from '../../../../Constants/ProjectConstants';
import { isAdmin } from '../../../../utils/share';
import { convertHM } from '../../../../utils/timeCalculation';
import { useProject } from '../../Layout/header/context';
import { MemberActionPopup } from './popups';
import { deleteProjectMember, editProjectMember, getProjectById } from '../../../../services/projectService';
import ProjectMemberFormModal from './popups/ProjectMemberFormModal';

const ProjectMembers = ({ onAddMemberClick, setLoading }) => {
  const classes = useStyles();
  const accountData = useSelector((state) => state?.login?.user || null);

  const { projectDetails } = useProject();
  const memberList = projectDetails?.projectmember;
  const projectRole = memberList?.find((data) => data.userId === accountData?.id)?.projectRoles?.roleName ?? '';
  const showAddMemberBtn = isAdmin() || PROJECT_EDITOR_ROLE.includes(projectRole);
  const isAddMemberBtnDisabled = projectDetails?.status !== 'active';
  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ pr: 3 }} justifyContent="space-between">
        <Typography variant="h4" color="GrayText">
          Project Members
        </Typography>
        <Stack direction="row" gap={5}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Badge
              className={classes.statusBadgeWrapper}
              overlap="circular"
              variant="dot"
              classes={{
                badge: `${classes.badge} ${classes.activeBadge}`,
              }}
            />
            <Typography variant="h5" color="GrayText">
              Active Members
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap={1}>
            <Badge
              className={classes.statusBadgeWrapper}
              overlap="circular"
              variant="dot"
              classes={{
                badge: `${classes.badge} ${classes.inactiveBadge}`,
              }}
            />
            <Typography variant="h5" color="GrayText">
              InActive Members
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Stack spacing={{ xs: 1, sm: 2 }} useFlexGap className={classes?.memberStack}>
        {showAddMemberBtn && (
          <AddMemberButton onClick={!isAddMemberBtnDisabled && onAddMemberClick} disabled={isAddMemberBtnDisabled} />
        )}
        {memberList?.length > 0 &&
          memberList.map((member) => (
            <MemberItem key={member.id} data={member} setLoading={setLoading} projectRole={projectRole} />
          ))}
      </Stack>
    </Stack>
  );
};

const AddMemberButton = (props) => {
  const classes = useStyles();
  return (
    <IconButton {...props} className={classes.memberButton}>
      <Box className={classes.addIcon}>
        <Add />
      </Box>
      <Typography variant="body2">Add Member</Typography>
    </IconButton>
  );
};

const MemberItem = ({ data, setLoading, projectRole }) => {
  const { id } = useParams();
  const { setProjectDetails, isEditPermission } = useProject();
  const classes = useStyles();
  const [openAction, setOpenAction] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);

  const handleOpenAction = useCallback((event) => isEditPermission && setOpenAction(event.currentTarget), []);
  const handleCloseAction = useCallback(() => setOpenAction(null), []);

  const handleOpenEdit = useCallback(() => {
    setOpenEdit(true);
    setOpenAction(null);
  }, []);
  const handleCloseEdit = useCallback(() => setOpenEdit(false), []);

  const handleRemoveMember = async () => {
    try {
      setLoading(true);
      handleCloseAction();
      const removeResponse = await deleteProjectMember(id, data?.id);
      const fetchResponse = await getProjectById(id);
      setProjectDetails(fetchResponse?.data?.data);
      toast.success(removeResponse?.data?.message);
    } catch (error) {
      toast.error('An error occurred while removing the project member. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectMemberUpdate = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      const response = await editProjectMember(values, id);
      const fetchResponse = await getProjectById(id);
      setProjectDetails(fetchResponse?.data?.data);
      toast.success(response?.data?.message);
      handleCloseEdit();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'An error occurred while updating the project member. Please try again.'
      );
      console.error(error);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const name = data?.user?.userName ?? '';
  const role = data?.projectRoles?.roleName ?? '';

  const btnDisabled = !isAdmin() && ROLE_LEVEL[projectRole] > ROLE_LEVEL[data?.projectRoles?.roleName];

  const projectPercentage = data?.userHours === 0 ? 0 : ((data?.totalTime / 3600) * 100) / data?.userHours;

  // Variable for handling the percentage value above 100%
  const circularProgressBarValue = Math.min(projectPercentage, 100);

  // Variable for handling the color in progress bar
  let circularProgressBarColor;
  switch (true) {
    case projectPercentage < 71:
      circularProgressBarColor = '#229A16';
      break;
    case projectPercentage < 91:
      circularProgressBarColor = '#FFC107';
      break;
    default:
      circularProgressBarColor = '#FF4842';
      break;
  }

  const usedHrs = convertHM(Math.round(parseInt(data?.totalTime, 10)));

  return (
    <>
      <IconButton className={classes.memberButton} onClick={handleOpenAction} disabled={btnDisabled}>
        <Tooltip title={role} placement="top" arrow>
          <Box className={classes?.avatarContainer}>
            <Avatar {...stringAvatar(name)} className={classes?.avatarIcon} />
            <CircularProgress
              variant="determinate"
              value={circularProgressBarValue}
              sx={{ color: circularProgressBarColor }}
              className={classes.circularProgress}
            />
          </Box>
        </Tooltip>
        <Box className={classes.memberTextContainer}>
          <Tooltip title={name} placement="top" arrow>
            <Typography
              noWrap
              variant="body1"
              textTransform="capitalize"
              align="left"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {name}
            </Typography>
          </Tooltip>
          {role !== GUEST && (
            <Stack direction="row" spacing={1}>
              <Typography noWrap variant="caption" align="left">
                {`${projectPercentage.toFixed()}%`}
              </Typography>
              <Typography noWrap variant="caption" align="left">
                {usedHrs} / {data?.userHours}h
              </Typography>
            </Stack>
          )}
        </Box>
        <Badge
          className={classes.statusBadgeWrapper}
          overlap="circular"
          variant="dot"
          classes={{
            badge: `${classes.badge} ${data?.isActive ? classes.activeBadge : classes.inactiveBadge}`,
          }}
        />
      </IconButton>
      <MemberActionPopup
        open={Boolean(openAction)}
        anchorEl={openAction}
        onClose={handleCloseAction}
        usedHrs={usedHrs}
        onRemove={handleRemoveMember}
        onEditClick={handleOpenEdit}
      />
      {openEdit && (
        <ProjectMemberFormModal
          open
          selected={data}
          initialValues={{
            userId: data?.userId,
            userHours: data?.userHours,
            id: data?.id,
            projectId: data?.projectId,
            projectRoleId: data?.projectRoleId,
            startDate: null,
            endDate: null,
          }}
          handleClose={handleCloseEdit}
          onSubmit={handleProjectMemberUpdate}
        />
      )}
    </>
  );
};

ProjectMembers.propTypes = {
  onAddMemberClick: PropTypes.func,
  setLoading: PropTypes.func,
};

AddMemberButton.propTypes = {
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};

MemberItem.propTypes = {
  data: PropTypes.object,
  setLoading: PropTypes.func,
  projectRole: PropTypes.string,
};

export default ProjectMembers;
